import { EquipmentType } from 'types/damage';
import * as FleetFile from 'types/fleet-files';
import { SizeTypeLookupOption } from 'types/size-types';

/*
 * One crosswalk row's editable decision, as the dialog holds it.
 *
 * The four fields are linked: a class narrows the targets on offer, choosing a target decides the
 * row, and "no single target" needs a note the gate user will read. Keeping them in one draft is why
 * editing happens in a dialog, not in grid cells (design D1). The server re-checks everything; these
 * helpers only keep the form honest.
 *
 * Targets come from the admin catalog list, which carries the row ids the server needs. This editor
 * is admin-only, so that list is always available here.
 */
export interface SizeTypeDraft {
  targetId: number | null;
  equipmentType: EquipmentType | null;
  /* "reviewed: there is no single right size type" — only meaningful without a target */
  noTarget: boolean;
  reviewNote: string;
}

export const emptyDraft = (): SizeTypeDraft => ({
  targetId: null,
  equipmentType: null,
  noTarget: false,
  reviewNote: ''
});

export const draftFrom = (row: FleetFile.SizeType): SizeTypeDraft => ({
  targetId: row.targetSizeType?.id ?? null,
  equipmentType: row.equipmentType ?? null,
  noTarget: !row.targetSizeType && row.reviewStatus === 'DECIDED',
  reviewNote: row.reviewNote ?? ''
});

/*
 * Targets the picker offers: active catalog rows of the row's class (all classes when the class is
 * not set), plus the row's current target even if it has retired since. Resubmitting an unchanged
 * retired target is allowed; choosing one anew is not.
 */
export const targetChoices = (
  catalog: SizeTypeLookupOption[],
  equipmentType: EquipmentType | null,
  currentTargetId?: number | null
): SizeTypeLookupOption[] =>
  catalog.filter(o => o.id === currentTargetId || (o.active && (!equipmentType || o.equipmentType === equipmentType)));

/* A problem the admin can fix before sending, or undefined. The server remains the authority. */
export const draftProblem = (draft: SizeTypeDraft): string | undefined =>
  !draft.targetId && draft.noTarget && !draft.reviewNote.trim()
    ? 'Say why there is no single size type. The gate user reads this note.'
    : undefined;

const decision = (draft: SizeTypeDraft) => ({
  targetSizeTypeId: draft.targetId,
  equipmentType: draft.equipmentType,
  reviewStatus: (draft.targetId || draft.noTarget ? 'DECIDED' : 'PENDING') as FleetFile.SizeTypeReviewStatus,
  reviewNote: draft.reviewNote.trim() || null
});

export const toUpdate = (draft: SizeTypeDraft, row: FleetFile.SizeType): FleetFile.SizeTypeMappingUpdate => ({
  ...decision(draft),
  version: row.version
});

export const toCreate = (
  code: number,
  description: string,
  iso: string,
  draft: SizeTypeDraft
): FleetFile.SizeTypeMappingCreate => ({
  code,
  description: description.trim(),
  iso: iso.trim() || null,
  ...decision(draft)
});
